import React, { useState, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { labelPaykeeperStatus } from '../../utils/paykeeperPaymentStatusLabels';

const formatMoney = (n) =>
    n != null
        ? Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '—';

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Таблица клиентов по платежам (магрегатор / аренда).
 * showTherapists — вторая таблица «кто сколько получил».
 */
const TherapyClientsTab = ({
    data,
    loading,
    title = 'Клиенты магрегатора',
    exportFilePrefix = 'клиенты_магрегатор',
    showTherapists = true,
    showTherapistInPayments = true
}) => {
    const [exporting, setExporting] = useState(false);
    const [expandedClients, setExpandedClients] = useState(() => new Set());

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner" />
            </div>
        );
    }

    const clients = data?.clients || [];
    const therapists = data?.therapists || [];
    const summary = data?.summary || {
        clients_count: 0,
        therapists_count: 0,
        payments_count: 0,
        total_amount: 0
    };

    const toggleClient = (key) => {
        setExpandedClients((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleExport = () => {
        if (!clients.length && !(showTherapists && therapists.length)) {
            toast.info('Нет данных для экспорта');
            return;
        }

        setExporting(true);
        try {
            const wb = XLSX.utils.book_new();

            const clientsHeaders = [
                'Клиент',
                'Сумма оплат',
                'Кол-во оплат',
                'Первая оплата',
                'Последняя оплата'
            ];
            const clientsRows = clients.map((row) => [
                row.client ?? '',
                row.total_amount != null ? Number(row.total_amount) : '',
                row.payments_count ?? 0,
                row.first_payment_date ? formatDate(row.first_payment_date) : '',
                row.last_payment_date ? formatDate(row.last_payment_date) : ''
            ]);
            XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.aoa_to_sheet([clientsHeaders, ...clientsRows]),
                'Клиенты'
            );

            if (showTherapists) {
                const therapistsHeaders = [
                    'Терапевт',
                    'Сумма от клиентов',
                    'Кол-во оплат',
                    'Кол-во клиентов'
                ];
                const therapistsRows = therapists.map((row) => [
                    row.therapist_name ?? '',
                    row.total_amount != null ? Number(row.total_amount) : '',
                    row.payments_count ?? 0,
                    row.clients_count ?? 0
                ]);
                XLSX.utils.book_append_sheet(
                    wb,
                    XLSX.utils.aoa_to_sheet([therapistsHeaders, ...therapistsRows]),
                    'Терапевты'
                );
            }

            const paymentsHeaders = [
                'Клиент',
                'Дата',
                'Сумма',
                'Возврат',
                'Итого',
                ...(showTherapistInPayments ? ['Терапевт'] : []),
                'Заказ',
                'PK ID',
                'Статус'
            ];
            const paymentsRows = clients.flatMap((client) =>
                (client.payments || []).map((p) => [
                    client.client ?? '',
                    p.payment_date ? formatDateTime(p.payment_date) : '',
                    p.pay_amount != null ? Number(p.pay_amount) : '',
                    p.refund_amount > 0 ? Number(p.refund_amount) : '',
                    p.amount != null ? Number(p.amount) : '',
                    ...(showTherapistInPayments ? [p.therapist_name ?? ''] : []),
                    p.orderid ?? '',
                    p.external_id ?? '',
                    labelPaykeeperStatus(p.status)
                ])
            );
            XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.aoa_to_sheet([paymentsHeaders, ...paymentsRows]),
                'Оплаты'
            );

            XLSX.writeFile(wb, `${exportFilePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success('Экспорт сохранён');
        } catch (error) {
            console.error(error);
            toast.error('Ошибка экспорта');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="data-table-container">
            <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                    <h2>{title}</h2>
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                        Клиентов: <strong>{summary.clients_count}</strong>
                        {' · '}
                        Оплат: <strong>{summary.payments_count}</strong>
                        {' · '}
                        Сумма: <strong>{formatMoney(summary.total_amount)} ₽</strong>
                        {showTherapists && (
                            <>
                                {' · '}
                                Терапевтов с оплатами: <strong>{summary.therapists_count || 0}</strong>
                            </>
                        )}
                        {summary.duplicates_fixed > 0 && (
                            <>
                                {' · '}
                                Исправлено дублей: <strong>{summary.duplicates_fixed}</strong>
                            </>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    className="action-btn complete-btn"
                    onClick={handleExport}
                    disabled={exporting || (!clients.length && !(showTherapists && therapists.length))}
                    title="Скачать XLSX"
                >
                    {exporting ? 'Экспорт…' : 'Экспорт XLSX'}
                </button>
            </div>

            <h3 style={{ margin: '24px 0 12px', fontSize: '18px' }}>Клиенты</h3>
            <table className="data-table">
                <thead>
                    <tr>
                        <th style={{ width: 40 }} />
                        <th>#</th>
                        <th>Клиент</th>
                        <th>Сумма оплат</th>
                        <th>Кол-во оплат</th>
                        <th>Первая оплата</th>
                        <th>Последняя оплата</th>
                    </tr>
                </thead>
                <tbody>
                    {clients.length > 0 ? (
                        clients.map((row, index) => {
                            const rowKey = `${row.client}-${index}`;
                            const isOpen = expandedClients.has(rowKey);
                            return (
                                <Fragment key={rowKey}>
                                    <tr>
                                        <td>
                                            <button
                                                type="button"
                                                className="action-btn pending-btn"
                                                onClick={() => toggleClient(rowKey)}
                                                title={isOpen ? 'Скрыть оплаты' : 'Показать оплаты'}
                                                aria-expanded={isOpen}
                                                style={{ padding: '4px 6px', lineHeight: 0 }}
                                            >
                                                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                            </button>
                                        </td>
                                        <td>{index + 1}</td>
                                        <td>{row.client}</td>
                                        <td>{formatMoney(row.total_amount)} ₽</td>
                                        <td>{row.payments_count}</td>
                                        <td>{formatDate(row.first_payment_date)}</td>
                                        <td>{formatDate(row.last_payment_date)}</td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="client-payments-detail-row">
                                            <td colSpan={7} style={{ background: '#f8f9fa', padding: '12px 16px' }}>
                                                {(row.payments || []).length > 0 ? (
                                                    <table className="data-table" style={{ margin: 0, background: '#fff' }}>
                                                        <thead>
                                                            <tr>
                                                                <th>Дата</th>
                                                                <th>Сумма</th>
                                                                {showTherapistInPayments && <th>Терапевт</th>}
                                                                <th>ФИО в платеже</th>
                                                                <th>Заказ</th>
                                                                <th>PK ID</th>
                                                                <th>Статус</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {row.payments.map((p) => (
                                                                <tr key={p.id || `${p.external_id}-${p.orderid}`}>
                                                                    <td>{formatDateTime(p.payment_date)}</td>
                                                                    <td>
                                                                        {formatMoney(p.amount)} ₽
                                                                        {p.refund_amount > 0 && (
                                                                            <span style={{ color: '#888', fontSize: '12px' }}>
                                                                                {' '}(возврат {formatMoney(p.refund_amount)})
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    {showTherapistInPayments && (
                                                                        <td>{p.therapist_name || '—'}</td>
                                                                    )}
                                                                    <td>{p.clientid_raw || '—'}</td>
                                                                    <td style={{ maxWidth: 360, wordBreak: 'break-word' }}>
                                                                        {p.orderid || '—'}
                                                                    </td>
                                                                    <td>{p.external_id ?? '—'}</td>
                                                                    <td>{labelPaykeeperStatus(p.status)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <span style={{ color: '#888' }}>Нет оплат</span>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={7} style={{ textAlign: 'center', color: '#888' }}>
                                Нет данных
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {showTherapists && (
                <>
                    <h3 style={{ margin: '32px 0 12px', fontSize: '18px' }}>Терапевты</h3>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Терапевт</th>
                                <th>Сумма от клиентов</th>
                                <th>Кол-во оплат</th>
                                <th>Кол-во клиентов</th>
                            </tr>
                        </thead>
                        <tbody>
                            {therapists.length > 0 ? (
                                therapists.map((row, index) => (
                                    <tr key={row.therapist_id || `${row.therapist_name}-${index}`}>
                                        <td>{index + 1}</td>
                                        <td>{row.therapist_name}</td>
                                        <td>{formatMoney(row.total_amount)} ₽</td>
                                        <td>{row.payments_count}</td>
                                        <td>{row.clients_count}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>
                                        Нет данных
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default TherapyClientsTab;
